import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Save, RefreshCw, RotateCcw, ChevronRight, Home, Layout, FileJson, AlertCircle, Check } from 'lucide-react';
import { systemPagesDefaults } from '../cms/systemPagesDefaults';

const SYSTEM_PAGES = [
  { id: 'site-content', name: 'Home / Landing Page', description: 'Main landing page content (Hero, Stats, Features, etc.)' },
  { id: 'features-page', name: 'Features Page', description: 'Features listing page content' },
  { id: 'about-page', name: 'About Page', description: 'About Us page content' },
  { id: 'contact-page', name: 'Contact Page', description: 'Contact page content and form settings' },
  { id: 'demo-page', name: 'Demo Page', description: 'Demo request page content' },
  { id: 'pricing-page', name: 'Pricing Page', description: 'Pricing page content (hero, CTA)' },
  { id: 'standards-page', name: 'Standards Page', description: 'ISO Standards overview page' },
  { id: 'resources-page', name: 'Resources Page', description: 'Resources library page' },
  { id: 'insights-page', name: 'Insights Page', description: 'Blog/Insights landing page content' },
];

const Breadcrumbs = ({ items }) => (
  <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
    <Link to="/cms" className="hover:text-gray-900 flex items-center">
      <Home size={14} className="mr-1" />
      CMS
    </Link>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        <ChevronRight size={14} />
        {item.href ? (
          <Link to={item.href} className="hover:text-gray-900">{item.label}</Link>
        ) : (
          <span className="text-gray-900 font-medium">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

const JsonEditor = ({ value, onChange }) => {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [error, setError] = useState(null);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);

  const handleChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    try {
      const parsed = JSON.parse(newText);
      setError(null);
      onChange(parsed);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-2">
      <textarea
        className={`w-full h-[600px] font-mono text-sm p-4 border rounded-md bg-slate-50 ${error ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary'}`}
        value={text}
        onChange={handleChange}
      />
      {error && (
        <div className="text-red-500 text-sm flex items-center gap-2">
          <AlertCircle size={14} />
          Invalid JSON: {error}
        </div>
      )}
      {!error && (
        <div className="text-green-600 text-sm flex items-center gap-2">
          <Check size={14} />
          Valid JSON
        </div>
      )}
    </div>
  );
};

const CMSSystemPages = () => {
  const [selectedPageId, setSelectedPageId] = useState(SYSTEM_PAGES[0].id);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');

  useEffect(() => {
    loadContent(selectedPageId);
  }, [selectedPageId]);

  const loadContent = async (pageId) => {
    try {
      setLoading(true);
      const docRef = doc(db, 'cms_content', pageId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setContent(docSnap.data());
      } else {
        // Fallback to defaults if available
        const defaults = systemPagesDefaults[pageId];
        if (defaults) {
          setContent(defaults);
          toast.info('Loaded default template (not saved yet)');
        } else {
          setContent({});
          toast.warning('No content found and no default template available');
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const docRef = doc(db, 'cms_content', selectedPageId);
      await setDoc(docRef, content);
      toast.success('Content saved successfully');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset to the default template? This will discard unsaved changes.')) {
      const defaults = systemPagesDefaults[selectedPageId];
      if (defaults) {
        setContent(defaults);
        toast.success('Reset to default template');
      } else {
        toast.error('No default template available');
      }
    }
  };

  const selectedPage = SYSTEM_PAGES.find(p => p.id === selectedPageId);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Breadcrumbs items={[{ label: 'System Pages' }]} />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">System Pages</h1>
            <p className="text-muted-foreground mt-1">Manage content for hardcoded system pages</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar List */}
          <Card className="lg:col-span-1 h-fit">
            <CardHeader>
              <CardTitle className="text-lg">Pages</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {SYSTEM_PAGES.map(page => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPageId(page.id)}
                    className={`w-full text-left px-4 py-3 rounded-md transition-colors ${
                      selectedPageId === page.id
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="text-sm font-medium">{page.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {page.description}
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Editor Area */}
          <Card className="lg:col-span-3">
            <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
              <div className="space-y-1">
                <CardTitle>{selectedPage?.name}</CardTitle>
                <CardDescription>ID: {selectedPage?.id}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleReset} title="Reset to Defaults">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button onClick={handleSave} disabled={saving || loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="editor" className="gap-2">
                      <FileJson className="h-4 w-4" />
                      JSON Editor
                    </TabsTrigger>
                    <TabsTrigger value="preview" className="gap-2" disabled>
                      <Layout className="h-4 w-4" />
                      Visual Editor (Coming Soon)
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="editor">
                    <div className="space-y-4">
                      <div className="bg-blue-50 text-blue-700 p-4 rounded-md text-sm flex items-start gap-2">
                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold">Advanced Editor</p>
                          <p>
                            You are editing the raw content structure. Please be careful to maintain the existing key names 
                            (e.g., "hero", "stats", "features") as the frontend relies on them.
                          </p>
                        </div>
                      </div>
                      <JsonEditor value={content} onChange={setContent} />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="preview">
                    <div className="py-20 text-center text-muted-foreground">
                      Visual editing for system pages is under development.
                      <br />
                      Please use the JSON Editor for now.
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CMSSystemPages;
